/**
 * Life4Earth V2 — React hook for the Impact Activity Catalog (DEV-1480).
 *
 * Loads the ENABLED activities — the acts that actually earn credit — grouped by
 * dimension, for DimensionRadar's "how to grow it" prompts.
 *
 * The catalog is platform-wide (not per-user), so it caches under one key.
 *
 * IMPORTANT for consumers: `byDimension[d]` may legitimately be an EMPTY array.
 * Today only Roots (5 acts) and Voice (1 act) have any enabled activities at all;
 * Mind, Body, Heart and Wings have none. A dimension with no enabled acts must
 * render its concept copy WITHOUT a "how to grow it" list — it must not fall back
 * to hardcoded suggestions, because those would promise credit that the emission
 * engine will not give. That silence is the honest state of the system.
 *
 * Pattern follows useApiImpactSignature.ts.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchEnabledActivities,
  groupByDimension,
  type ImpactActivity,
  type DimKey,
} from "./impactActivities";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

const CACHE_KEY = "impactActivities:enabled";

export interface UseApiImpactActivitiesResult {
  /** Every enabled activity, flat. */
  activities: ImpactActivity[];
  /** Enabled activities grouped by dimension. A dimension may be empty — see note above. */
  byDimension: Record<DimKey, ImpactActivity[]>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY_BY_DIM: Record<DimKey, ImpactActivity[]> = {
  mind: [], voice: [], body: [], heart: [], wings: [], roots: [],
};

export function useApiImpactActivities(): UseApiImpactActivitiesResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiImpactActivitiesResult>({
    activities: [],
    byDimension: EMPTY_BY_DIM,
    loading: true,
    error: null,
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const list = await fetchForKey(
        CACHE_KEY,
        async (): Promise<ImpactActivity[]> => fetchEnabledActivities(),
        { force: true },
      );
      if (!mountedRef.current) return;

      setData({
        activities: list,
        byDimension: groupByDimension(list),
        loading: false,
        error: null,
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      // On failure we surface EMPTY, never a hardcoded fallback list. A prompt
      // that survives an API failure is a prompt nobody can verify against the
      // engine — better to show nothing than to show a promise we can't keep.
      setData({
        activities: [],
        byDimension: EMPTY_BY_DIM,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load impact activities",
        refetch,
      });
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refetch();
    return () => {
      mountedRef.current = false;
    };
  }, [refetch]);

  // A sysadmin toggling an activity invalidates this key; pick the change up live.
  useEffect(() => {
    const handleCacheUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === CACHE_KEY) refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
  }, [refetch]);

  return data;
}
